import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../../assets/logo.png';
import { CurrencyToggle } from './CurrencyToggle.jsx';

export const ROUTE_BY_KEY = {
  home: '/',
  solutions: '/services',
  opensource: '/opensource',
  cases: '/use-cases',
  blog: '/resources',
  contact: '/contact',
};

// The real Aakeeo logo is a horizontal lockup (mark + wordmark baked into one PNG).
// Natural size is 1061x538. Pass both explicit width and height so the browser
// reserves space before the image loads — prevents the nav from reflowing (CLS).
const LOGO_ASPECT = 1061 / 538;

export function BrandLogo({ height = 32 }) {
  const width = Math.round(height * LOGO_ASPECT);
  return (
    <img
      src={logo}
      alt="Aakeeo Technologies"
      width={width}
      height={height}
      style={{ display: 'block', flexShrink: 0 }}
    />
  );
}

export function NavBar({ page }) {
  const items = [
    { key: 'home', label: 'Home' },
    { key: 'solutions', label: 'Solutions' },
    { key: 'opensource', label: 'Open Source' },
    { key: 'cases', label: 'Use Cases' },
    { key: 'blog', label: 'Resources' },
    { key: 'contact', label: 'Contact' },
  ];

  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Close the drawer on route change.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <header className={'navA' + (open ? ' is-open' : '')}>
      <div className="navA-inner">
        <Link className="navA-brand" to="/" onClick={close}>
          <BrandLogo height={70} />
        </Link>
        <button
          type="button"
          className="navA-toggle"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <span className="navA-toggle-bar" />
          <span className="navA-toggle-bar" />
          <span className="navA-toggle-bar" />
        </button>
        <nav className="navA-items" id="primary-navigation">
          {items.map((it) => (
            <Link
              key={it.key}
              className={'navA-item' + (page === it.key ? ' is-active' : '')}
              to={ROUTE_BY_KEY[it.key]}
              onClick={close}
            >
              {it.label}
            </Link>
          ))}
          <Link className="btnA-primary navA-cta" to="/contact" onClick={close}>
            Book a call <span aria-hidden>→</span>
          </Link>
        </nav>
      </div>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="footA">
      <div className="footA-grid">
        <div className="footA-brand">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <BrandLogo height={44} />
          </div>
          <p className="footA-tag">Technologies delivered — CRMs you own, built on open source, for teams that outgrew subscriptions.</p>
          <p className="footA-addr">Bengaluru, India · sales@aakeeo.com · +91 8431 748 754</p>
        </div>
        <div>
          <h3>Ecosystem</h3>
          <Link to="/">Home</Link>
          <Link to="/services">Enterprise Solutions</Link>
          <Link to="/opensource">Open Source</Link>
          <Link to="/use-cases">Use Cases</Link>
          <Link to="/resources">Resources</Link>
        </div>
        <div>
          <h3>Services</h3>
          <Link to="/services/suitecrm-implementation">SuiteCRM Implementation</Link>
          <Link to="/services/suitecrm-customisation">Customisation</Link>
          <Link to="/services/crm-integration">CRM Integration</Link>
          <Link to="/services/ai-workflow-automation">AI Workflow Automation</Link>
          <Link to="/services/suitecrm-support">Support &amp; Maintenance</Link>
        </div>
        <div>
          <h3>Connect</h3>
          <a href="https://www.linkedin.com/company/aakeeo-technologies-pvt-ltd" target="_blank" rel="noopener noreferrer">LinkedIn</a>
          <a href="https://x.com/AakeeoTech" target="_blank" rel="noopener noreferrer">X (Twitter)</a>
          <a href="https://github.com/Aakeeo" target="_blank" rel="noopener noreferrer">GitHub</a>
          <Link to="/contact">Book a strategy call</Link>
        </div>
      </div>
      <div className="footA-bot">
        <span>© {new Date().getFullYear()} Aakeeo Technologies Pvt Ltd</span>
        <CurrencyToggle />
        <span>Open source, end to end.</span>
      </div>
    </footer>
  );
}

export function Placeholder({ label = 'product shot', ratio = '16 / 10', dark = false }) {
  const bg = dark
    ? 'linear-gradient(135deg, rgba(255,255,255,0.03) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.03) 50%, rgba(255,255,255,0.03) 75%, transparent 75%)'
    : 'linear-gradient(135deg, rgba(12,26,54,0.05) 25%, transparent 25%, transparent 50%, rgba(12,26,54,0.05) 50%, rgba(12,26,54,0.05) 75%, transparent 75%)';
  const border = dark ? 'rgba(255,255,255,0.09)' : 'rgba(12,26,54,0.12)';
  return (
    <div
      style={{
        aspectRatio: ratio,
        background: bg,
        backgroundSize: '14px 14px',
        border: `1px solid ${border}`,
        borderRadius: dark ? 4 : 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'var(--font-mono)',
        fontSize: 11,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: dark ? 'rgba(255,255,255,0.4)' : 'rgba(12,26,54,0.4)',
        width: '100%',
      }}
    >
      [ {label} ]
    </div>
  );
}
